import {ENDPOINTS} from './endpoints';
export const CONTROL_API_URL = ENDPOINTS.control;

// Sessions use the selected environment's cookie domain. Authenticated checks
// must run on its deployed app origin, which can receive that HttpOnly cookie.

export interface Account {
  readonly id: string;
  readonly email: string;
  readonly createdAt: number;
  /** Null until the person chooses (doc/20261023-i18n-zh-cn.md §3.2); absent from an older control plane. */
  readonly displayLanguage?: string | null;
  readonly displayLanguageChosenAt?: number;
}

/** What the console needs to choose and stamp a language (§3.3); absent from an older control plane. */
export interface LanguageFacts {
  readonly supportedDisplayLanguages?: readonly string[];
  readonly serverNow?: number;
}

export interface DisplayLanguageSave {
  readonly applied: boolean;
  readonly displayLanguage: string | null;
  readonly chosenAt: number;
}

export type ZoneStatus = 'awaiting_setup' | 'claimed' | 'connected';
export type ZoneTopology = 'laptop' | 'firm' | 'cloud';

export interface Zone {
  readonly id: string;
  readonly name: string;
  readonly status: ZoneStatus;
  readonly claimExpiresAt: number | null;
  readonly claimedAt: number | null;
  readonly lastCheckinAt: number | null;
  readonly createdAt: number;
  readonly configVersion: number;
  readonly pendingRotationSince: number | null;
  /** Absent from a Worker that predates topologies (multi-user spec §3.10): a laptop. */
  readonly topology?: ZoneTopology;
  /** The deploy-time owner of a firm zone; the zone's own directory holds the current one. */
  readonly ownerEmail?: string | null;
  readonly zoneHost?: string | null;
  /** Active users as of the last check-in; counts only. */
  readonly userCount?: number | null;
  /** A cloud zone's tenant and power (doc/20261018-cloud-zone-browser.md §3.2.1). */
  readonly cloud?: CloudFacts;
  /**
   * The zone's latest storage report (doc/20261020-zone-storage-budget.md §7); `unknown` once two days old. Null before
   * any; absent from older Workers.
   */
  readonly storage?: ZoneStorageReport | null;
  /** The zone handle (readable intake addresses §3.1); null for a zone set up before handles, absent from older Workers. */
  readonly handle?: string | null;
}

export interface ZoneStorageReport {
  readonly usedBytes: number;
  readonly budgetBytes: number;
  readonly observedAt: number;
  readonly state: 'ok' | 'warn80' | 'warn95' | 'over' | 'unknown';
  readonly reportedAt: number;
}

export interface CloudRegion { readonly id: string; readonly name: string; readonly place: string }
export type CloudOffer = { readonly enabled: false } | {
  readonly enabled: true; readonly tenantDomain: string; readonly regions: readonly CloudRegion[];
  /** The control plane's preselection (part 2 §4 I3); absent from a Worker that predates it. */
  readonly suggestedRegion?: string | null;
};
export type CloudLifecycle = 'provisioning' | 'failed' | 'active' | 'dormant' | 'frozen' | 'deletion_pending' | 'deleted';
export type CloudPower = 'awake' | 'quiescing' | 'releasing' | 'asleep' | 'waking';
/** ISO weekdays (1 = Monday), a local HH:MM, and an IANA time zone. */
export interface PrewarmSchedule { readonly days: readonly number[]; readonly at: string; readonly timeZone: string }

export interface CloudFacts {
  readonly region: string;
  readonly tenantName: string;
  readonly webName: string;
  readonly directName: string;
  readonly lifecycle: CloudLifecycle;
  readonly power: CloudPower | null;
  readonly powerAt: number | null;
  readonly powerError: string | null;
  /** `restoring` while a thaw or an availability-zone restore runs (freeze/thaw spec §3.8); absent from older Workers. */
  readonly powerReason?: 'restoring' | null;
  readonly prewarm: { readonly schedule: PrewarmSchedule; readonly next: number | null } | null;
  readonly lastUsedAt: number | null;
  readonly idleMinutes: number;
  readonly error: 'capacity' | 'internal' | null;
  /** Due to freeze, but its last release brought no backup to freeze from (storage budget §5.3); absent from older Workers. */
  readonly freezeBlocked?: 'no_backup' | null;
}

export type FirmPlatform = 'macos' | 'windows' | 'linux';

/** One numbered firm install step (spec §3.10, reference design P5). Backticks mark code. */
export interface InstallStep {
  readonly title: string;
  readonly text?: string;
  readonly body?: string;
  readonly command?: true;
  readonly note?: string;
}

export interface FirmInstructions {
  readonly platforms: Readonly<Record<FirmPlatform, readonly InstallStep[]>>;
  readonly expiry: string;
}

export interface InstallCommand {
  readonly claimToken: string;
  readonly claimExpiresAt?: number;
  readonly installCommand: string;
  readonly zone?: Zone;
  /** A firm zone's steps; a laptop has none. */
  readonly instructions?: FirmInstructions;
}

export interface CreateZoneRequest {
  readonly name: string;
  readonly topology?: 'laptop' | 'firm' | 'cloud';
  readonly ownerEmail?: string;
  readonly zoneHost?: string;
  readonly tenantName?: string;
  /** Every topology (readable intake addresses §3.1.3); a cloud create sends it equal to `tenantName`. */
  readonly handle?: string;
  readonly region?: string;
}

/** A laptop or firm create answers its install command; a cloud create answers the zone alone (nothing to install). */
export interface CreatedZone extends Partial<InstallCommand> {
  readonly zone: Zone;
}

/** The public wake API's answer, with when to try again on 429 (§3.1.2, §3.1.3). */
export interface WakeAnswer<T> { readonly status: number; readonly body: T | null; readonly retryAfterSeconds: number | null }

async function publicWake<T>(path: string, init: RequestInit = {}): Promise<WakeAnswer<T>> {
  // The wake API answers CORS without credentials: the wait page needs no sign-in (§3.2.4).
  const response = await fetch(`${CONTROL_API_URL}${path}`, { ...init, credentials: 'omit', cache: 'no-store' });
  const body = await response.json().catch(() => null) as T | null;
  const retry = Number(response.headers.get('retry-after'));
  return { status: response.status, body, retryAfterSeconds: response.headers.has('retry-after') && Number.isFinite(retry) ? retry : null };
}

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string) {
    super(code);
    // eslint-disable-next-line i18next/no-literal-string -- the error class name, for logs
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${CONTROL_API_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers: init.body === undefined
      ? init.headers
      : { 'content-type': 'application/json', ...init.headers },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: unknown } | null;
    throw new ApiError(
      response.status,
      typeof body?.error === 'string' ? body.error : 'request_failed',
    );
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const controlApi = {
  // `locale` is the language the auth screen shows: the code's email is written in it (i18n spec §3.3).
  issueCode: (email: string, locale?: string) => request<{ ok: true }>('/v1/auth/code', {
    method: 'POST', body: JSON.stringify(locale === undefined ? { email } : { email, locale }),
  }),
  verifyCode: (email: string, code: string) => request<{ account: Account }>('/v1/auth/verify', {
    method: 'POST', body: JSON.stringify({ email, code }),
  }),
  session: () => request<{ account: Account } & LanguageFacts>('/v1/session'),
  saveDisplayLanguage: (displayLanguage: string, chosenAt: number) => request<DisplayLanguageSave>('/v1/account/display-language', {
    method: 'PUT', body: JSON.stringify({ displayLanguage, chosenAt }),
  }),
  logout: () => request<void>('/v1/session', { method: 'DELETE' }),
  listZones: () => request<{ zones: Zone[] }>('/v1/zones'),
  // A laptop sends no topology, so it works against either Worker (spec §3.10); a handle only once the Worker
  // answers the handle check (readable intake addresses §3.1.3).
  createZone: (zone: CreateZoneRequest) => request<CreatedZone>('/v1/zones', {
    method: 'POST', body: JSON.stringify(zone.topology === 'firm' || zone.topology === 'cloud' ? zone
      : { name: zone.name, ...(zone.handle === undefined ? {} : { handle: zone.handle }) }),
  }),
  // 404 from a Worker that predates handles (readable intake addresses §3.1.3).
  handleAvailable: (handle: string, signal?: AbortSignal) => request<{ available: boolean }>(
    `/v1/zones/handles/${encodeURIComponent(handle)}`, signal === undefined ? {} : { signal },
  ),
  cloudOffer: () => request<CloudOffer>('/v1/cloud'),
  wakeZone: (id: string) => request<{ power: CloudPower }>(`/v1/zones/${encodeURIComponent(id)}/wake`, { method: 'POST' }),
  sleepZone: (id: string) => request<{ power: CloudPower; sleep?: 'requested' }>(`/v1/zones/${encodeURIComponent(id)}/sleep`, { method: 'POST' }),
  setPrewarm: (id: string, schedule: PrewarmSchedule | null) => request<{ prewarm: CloudFacts['prewarm'] }>(
    `/v1/zones/${encodeURIComponent(id)}/prewarm`, { method: 'PUT', body: JSON.stringify({ schedule }) },
  ),
  // The wait page's public calls (§3.2.4).
  wakeState: (tenant: string) => publicWake<{ state: 'awake' | 'waking' | 'asleep'; webAddress: string; reason?: 'restoring' | 'frozen' | null }>(
    `/v1/wake?tenant=${encodeURIComponent(tenant)}`,
  ),
  addressWake: (tenant: string, signal?: AbortSignal) => publicWake<{ power: CloudPower }>('/v1/wake', {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ tenant }), ...(signal === undefined ? {} : { signal }),
  }),
  zone: (id: string) => request<{ zone: Zone }>(`/v1/zones/${encodeURIComponent(id)}`),
  deleteZone: (id: string) => request<void>(`/v1/zones/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  }),
  // The account's zones with backups and each one's last success (backup spec §4.7).
  backups: () => request<{ zones: readonly { readonly zoneId: string; readonly lastSuccessAt: number | null }[] }>('/v1/backups'),
  regenerateClaimToken: (id: string) => request<InstallCommand>(
    `/v1/zones/${encodeURIComponent(id)}/claim-token`,
    { method: 'POST' },
  ),
  rotateToken: (id: string) => request<{ configVersion: number }>(
    `/v1/zones/${encodeURIComponent(id)}/rotate-token`, { method: 'POST' },
  ),
  forceRevokeToken: (id: string) => request<{ configVersion: number }>(
    `/v1/zones/${encodeURIComponent(id)}/force-revoke-token`, {
      method: 'POST', body: JSON.stringify({ confirm: id }),
    },
  ),
};
